/**
 * SCHED-FLOW-01 — the availability grid (step 2 of Schedule a Visit).
 * Days across, arrival windows down; every cell is the best-fitting tech for
 * that day and window, already scored by the copilot's board math
 * (availabilityGrid in suggestTimes.ts). Green = lands on a route with almost
 * no extra driving, amber = fits but adds miles, "—" = nobody free, "full" =
 * the day is booked out. Click a cell to pick it, click another to swap, click
 * a day's header to cross it off when the customer says no — and a WINDOW's
 * header the same way (SCHED-AVAIL-01, Seth 2026-09-17: the five customer
 * chips were "so specific and don't encompass everything"; crossing off any
 * day or window covers every "can't do" a customer can say, nothing pre-set).
 * `lightDays` (Routine) tags the lightest days in view.
 * SCHED-WEATHER-01 (Seth 2026-09-22: "would it be cool to have a high/low
 * temp, weather for that day?"): each day header carries the forecast high /
 * low and a weather glyph, quietly — the same Open-Meteo read Today uses,
 * sixteen days out. A hot (88°+) or hard-freeze (≤ 10°) day gets the one
 * color it earns, because those are the days the phones ring.
 */
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { cellKey, type TimeSuggestion } from "@/features/board/suggestTimes";
import { useForecast, iconFor } from "@/features/today/WeatherOutlook";

export interface GridSelection { empno: string; dateISO: string; window: string }

function dayParts(iso: string): { dow: string; md: string } {
  const d = new Date(`${iso}T00:00:00`);
  return {
    dow: d.toLocaleDateString("en-US", { weekday: "short" }),
    md: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
}

export function fitOf(o: TimeSuggestion): "great" | "ok" {
  return o.stopCount === 0 || o.addedMiles <= 3 ? "great" : "ok";
}

export function AvailabilityGrid({
  days,
  windows,
  cells,
  openDays,
  loading,
  selected,
  crossed,
  crossedWindows,
  lightDays,
  today,
  onToggleDay,
  onToggleWindow,
  onPick,
  onEarlier,
  onLater,
  canEarlier,
  techFirst,
  windowLabel,
  laterWindows,
  laterOpen = false,
  onToggleLater,
  afterHours,
}: {
  days: string[];
  windows: string[];
  cells: Map<string, TimeSuggestion[]> | null;
  openDays: Set<string>;
  loading: boolean;
  selected: GridSelection | null;
  crossed: Set<string>;
  /** Arrival windows the customer can't do — the whole row is crossed off. */
  crossedWindows: Set<string>;
  /** Routine: the lightest open days in view get a small tag. */
  lightDays?: Set<string>;
  today: string;
  onToggleDay: (day: string) => void;
  onToggleWindow: (window: string) => void;
  onPick: (o: TimeSuggestion) => void;
  onEarlier: () => void;
  onLater: () => void;
  canEarlier: boolean;
  techFirst: (empno: string) => string;
  windowLabel: (w: string) => string;
  /** Rarely used windows (3–7) folded into one "Evening & After Hours" row
   *  until it is opened (Seth 2026-09-23: "they are rarely used"). */
  laterWindows?: string[];
  laterOpen?: boolean;
  onToggleLater?: () => void;
  /** The After Hours row: that night's published on-call pair per day. A
   *  pick books the AFTER window with that tech (the after-hours fee). */
  afterHours?: {
    byDay: Map<string, { primary: string; backup: string | null }>;
    onPick: (empno: string, dateISO: string) => void;
  } | null;
}) {
  const forecast = useForecast(16).data;
  const wx = (d: string) => forecast?.find((f) => f.date === d) ?? null;
  const later = new Set(laterWindows ?? []);
  const dayRows = windows.filter((w) => !later.has(w));
  const lateRows = windows.filter((w) => later.has(w));
  const hasFold = lateRows.length > 0 || !!afterHours;
  const renderRow = (w: string) => {
              const rowOff = crossedWindows.has(w);
              return (
              <tr key={w}>
                <th className="border-b border-r border-hairline bg-slate-50 p-0 text-left">
                  <button
                    type="button"
                    onClick={() => onToggleWindow(w)}
                    title={rowOff ? "Crossed off — click to bring this window back" : "Customer can't do this window? Click to cross it off"}
                    className={cn("block w-full px-2 py-1 text-left text-[12px] font-semibold transition-colors hover:bg-slate-100", rowOff ? "text-slate-300 line-through" : "text-slate-700")}
                  >
                    {windowLabel(w)}
                  </button>
                </th>
                {days.map((d) => {
                  const off = crossed.has(d) || rowOff;
                  const opts = cells?.get(cellKey(d, w)) ?? [];
                  const best = opts[0] ?? null;
                  const selOpt = selected && selected.dateISO === d && selected.window === w ? opts.find((o) => o.empno === selected.empno) ?? null : null;
                  const isSel = !!selOpt;
                  const shown = selOpt ?? best;
                  if (off) {
                    return (
                      <td key={d} className="h-11 border-b border-r border-hairline p-0 text-center align-middle last:border-r-0" style={{ background: "repeating-linear-gradient(135deg, transparent 0 6px, #f1f5f9 6px 8px)" }}>
                        <span className="text-[11px] text-slate-300">can't</span>
                      </td>
                    );
                  }
                  if (!shown) {
                    const full = cells && !openDays.has(d);
                    return (
                      <td key={d} className="h-11 border-b border-r border-hairline p-0 text-center align-middle last:border-r-0">
                        <span className="text-[11px] text-slate-300">{loading && !cells ? "…" : full ? "full" : "—"}</span>
                      </td>
                    );
                  }
                  const fit = fitOf(shown);
                  const others = opts.filter((o) => o.empno !== shown.empno);
                  return (
                    <td key={d} className="h-12 border-b border-r border-hairline p-0.5 text-center align-middle last:border-r-0">
                      <button
                        type="button"
                        onClick={() => onPick(shown)}
                        title={`${shown.techName} · ${shown.dayLabel} · ${windowLabel(w)} — ${shown.placement}, ${shown.addedMiles < 0.5 ? "no extra miles" : `+${Math.round(shown.addedMiles)} mi`}${others.length ? ` · also ${others.map((o) => `${techFirst(o.empno)} +${Math.round(o.addedMiles)}`).join(", ")}` : ""}`}
                        className={cn(
                          "inline-flex max-w-full flex-col items-center rounded-lg px-1.5 py-0.5 text-[12px] font-semibold leading-tight transition-colors",
                          isSel
                            ? "bg-brand-600 text-white shadow ring-2 ring-brand-200"
                            : "text-slate-700 hover:bg-brand-50",
                        )}
                      >
                        <span className="inline-flex max-w-full items-center gap-1.5">
                          <span className={cn("h-2 w-2 shrink-0 rounded-full", isSel ? "bg-white" : fit === "great" ? "bg-emerald-500" : "bg-amber-500")} />
                          <span className="truncate">{techFirst(shown.empno)}</span>
                        </span>
                        <span className={cn("text-[10.5px] font-medium tabular-nums", isSel ? "text-brand-100" : "text-slate-400")}>{shown.addedMiles < 0.5 ? "no extra miles" : `+${Math.round(shown.addedMiles)} mi`}</span>
                      </button>
                    </td>
                  );
                })}
              </tr>
              );
  };
  return (
    <div className="rounded-xl border border-hairline bg-white">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-[12px]" style={{ minWidth: `${76 + days.length * 84}px` }}>
          <thead>
            <tr>
              <th className="w-[76px] border-b border-r border-hairline bg-slate-50 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Window</th>
              {days.map((d) => {
                const p = dayParts(d);
                const off = crossed.has(d);
                return (
                  <th key={d} className={cn("border-b border-r border-hairline bg-slate-50 p-0 last:border-r-0")}>
                    <button
                      type="button"
                      onClick={() => onToggleDay(d)}
                      title={off ? "Crossed off — click to bring the day back" : "Customer can't do this day? Click to cross it off"}
                      className={cn(
                        "flex w-full flex-col items-center px-1 py-1.5 leading-tight transition-colors hover:bg-slate-100",
                        off ? "text-slate-300 line-through" : "text-slate-700",
                      )}
                    >
                      <span className={cn("text-[12px] font-semibold", d === today && !off && "text-brand-700")}>{d === today ? "Today" : p.dow}</span>
                      <span className="text-[11px] text-slate-400">{p.md}</span>
                      {(() => {
                        const f = wx(d);
                        if (!f || off) return null;
                        const Icon = iconFor(f.code);
                        const hot = f.high >= 88;
                        const cold = f.high <= 10;
                        return (
                          <span
                            className={cn("mt-0.5 inline-flex items-center gap-1 text-[11px] tabular-nums", hot ? "font-semibold text-amber-700" : cold ? "font-semibold text-sky-700" : "text-slate-400")}
                            title={`${f.high}° / ${f.low}°${f.precip >= 40 ? ` · ${f.precip}% chance of precipitation` : ""}${hot ? " — a no-cool day; the phones will ring" : cold ? " — a no-heat day; the phones will ring" : ""}`}
                          >
                            <Icon className="h-3 w-3" />
                            {f.high}°<span className="opacity-70">/{f.low}°</span>
                          </span>
                        );
                      })()}
                      {!off && lightDays?.has(d) && (
                        <span className="mt-0.5 rounded-full bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700" title="One of the lightest days in view — a routine call fits here without crowding anyone">light day</span>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {dayRows.map(renderRow)}
            {hasFold && (
              <tr>
                <td colSpan={days.length + 1} className="border-b border-hairline p-0">
                  <button
                    type="button"
                    onClick={onToggleLater}
                    aria-expanded={laterOpen}
                    title={laterOpen ? "Fold the evening rows away" : "Show the 3–7 window and the on-call techs for after hours"}
                    className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-[12px] font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800"
                  >
                    {laterOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    Evening &amp; After Hours
                    {!laterOpen && (
                      <span className="font-normal text-slate-400">
                        — {[...lateRows.map((w) => windowLabel(w)), afterHours ? "on call" : null].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </button>
                </td>
              </tr>
            )}
            {laterOpen && lateRows.map(renderRow)}
            {laterOpen && afterHours && (
              <tr key="AFTER">
                <th className="border-b border-r border-hairline bg-slate-50 px-2 py-1 text-left">
                  <span className="block text-[12px] font-semibold text-slate-700">After Hours</span>
                  <span className="block text-[10.5px] font-medium text-slate-400">on call</span>
                </th>
                {days.map((d) => {
                  const pair = afterHours.byDay.get(d);
                  if (crossed.has(d)) {
                    return (
                      <td key={d} className="h-11 border-b border-r border-hairline p-0 text-center align-middle last:border-r-0" style={{ background: "repeating-linear-gradient(135deg, transparent 0 6px, #f1f5f9 6px 8px)" }}>
                        <span className="text-[11px] text-slate-300">can't</span>
                      </td>
                    );
                  }
                  if (!pair) {
                    return (
                      <td key={d} className="h-11 border-b border-r border-hairline p-0 text-center align-middle last:border-r-0">
                        <span className="text-[11px] text-slate-300" title="No on-call schedule published for this night">—</span>
                      </td>
                    );
                  }
                  const selEmp = selected && selected.window === "AFTER" && selected.dateISO === d ? selected.empno : null;
                  const pick = (e: string, role: string) => (
                    <button
                      type="button"
                      onClick={() => afterHours.onPick(e, d)}
                      title={`${techFirst(e)} is the ${role} on call that night — books After Hours (the after-hours fee)`}
                      className={cn(
                        "rounded-lg px-1.5 py-0.5 leading-tight transition-colors",
                        role === "primary" ? "text-[12px] font-semibold" : "text-[11px] font-medium",
                        selEmp === e ? "bg-brand-600 text-white shadow ring-2 ring-brand-200" : role === "primary" ? "text-slate-700 hover:bg-brand-50" : "text-slate-400 hover:bg-brand-50 hover:text-slate-700",
                      )}
                    >
                      {role === "backup" ? `+ ${techFirst(e)}` : techFirst(e)}
                    </button>
                  );
                  return (
                    <td key={d} className="h-12 border-b border-r border-hairline p-0.5 text-center align-middle last:border-r-0">
                      <div className="flex flex-col items-center">
                        {pick(pair.primary, "primary")}
                        {pair.backup && pick(pair.backup, "backup")}
                      </div>
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-1.5 text-[11px] text-slate-500">
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" /> lands on a route, almost no extra driving
          <span className="ml-3 mr-1 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" /> fits, adds miles
          <span className="ml-3">— nobody free · full = booked out · +N = extra miles · click a day or window header to cross it off</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <button type="button" onClick={onEarlier} disabled={!canEarlier} className="inline-flex items-center rounded-md px-1.5 py-0.5 font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /> earlier</button>
          <button type="button" onClick={onLater} className="inline-flex items-center rounded-md px-1.5 py-0.5 font-semibold text-brand-700 hover:bg-brand-50">later <ChevronRight className="h-3.5 w-3.5" /></button>
        </span>
      </div>
    </div>
  );
}
